import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import { gql, useMutation } from '@apollo/client';
import type { BusinessOfferBooking } from '../../types/business';
import RoomSelector from './partner/RoomSelector';
import RoomAssignment from './partner/RoomAssignment';
import TravelerForm from './partner/TravelerForm';

const CREATE_BOOKING = gql`
  mutation CreateBooking($offerId: Int!, $input: BusinessOfferBookingInput!) {
    createBusinessOfferBooking(offerId: $offerId, input: $input) {
      id
      status
      travelerInfo
      roomPreferences
      createdAt
    }
  }
`;

const bookingSchema = z.object({
  travelers: z.array(z.object({
    firstName: z.string().min(2, 'Le prénom est requis'),
    lastName: z.string().min(2, 'Le nom est requis'),
    email: z.string().email('Email invalide'),
    phone: z.string().min(8, 'Numéro de téléphone invalide'),
  })).min(1, 'Au moins un voyageur est requis'),
  roomPreferences: z.array(z.object({
    type: z.enum(['SINGLE', 'DOUBLE', 'TRIPLE', 'SUITE']),
    count: z.number().min(1),
  })),
  specialRequests: z.string().optional(),
});

type BookingFormData = z.infer<typeof bookingSchema>;

interface BusinessOfferBookingFormProps {
  isOpen: boolean;
  onClose: () => void;
  offerId: number;
  minimumParticipants: number;
}

type Step = 'rooms' | 'travelers' | 'assignment';

export default function BusinessOfferBookingForm({
  isOpen,
  onClose,
  offerId,
  minimumParticipants,
}: BusinessOfferBookingFormProps) {
  const [currentStep, setCurrentStep] = useState<Step>('rooms');
  const [roomAssignments, setRoomAssignments] = useState<Record<string, string[]>>({});

  const [createBooking, { loading }] = useMutation(CREATE_BOOKING, {
    onCompleted: () => {
      onClose();
    },
    onError: (error) => {
      alert(`Erreur lors de la création de la réservation : ${error.message}`);
    }
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      travelers: Array(minimumParticipants).fill({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      }),
      roomPreferences: [{
        type: 'DOUBLE',
        count: 1,
      }],
    },
  });

  const roomPreferences = watch('roomPreferences');
  const travelers = watch('travelers');

  const totalCapacity = roomPreferences.reduce((sum, room) => {
    const capacity = room.type === 'SINGLE' ? 1 : 
                    room.type === 'DOUBLE' ? 2 :
                    room.type === 'TRIPLE' ? 3 : 2;
    return sum + (capacity * room.count);
  }, 0);

  const handleRoomPreferencesChange = (rooms: typeof roomPreferences) => {
    setValue('roomPreferences', rooms);
    // Réinitialiser les attributions de chambres
    setRoomAssignments({});
  };

  const onSubmit = async (data: BookingFormData) => {
    try {
      await createBooking({
        variables: {
          offerId,
          input: data,
        },
      });
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Réservation</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Étapes de progression */}
            <div className="flex justify-between items-center mb-8">
              {(['rooms', 'travelers', 'assignment'] as const).map((step, index) => (
                <React.Fragment key={step}>
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      currentStep === step ? 'bg-blue-600 text-white' :
                      index < ['rooms', 'travelers', 'assignment'].indexOf(currentStep) ? 'bg-green-600 text-white' :
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {index + 1}
                    </div>
                    <span className="ml-2 font-medium">
                      {step === 'rooms' ? 'Chambres' :
                       step === 'travelers' ? 'Voyageurs' :
                       'Attribution'}
                    </span>
                  </div>
                  {index < 2 && (
                    <div className="flex-1 mx-4 h-0.5 bg-gray-200" />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Contenu selon l'étape */}
            {currentStep === 'rooms' && (
              <div>
                <h3 className="text-lg font-medium mb-4">Sélection des chambres</h3>
                <RoomSelector
                  roomPreferences={roomPreferences}
                  onChange={handleRoomPreferencesChange}
                  maxTravelers={minimumParticipants}
                />
              </div>
            )}

            {currentStep === 'travelers' && (
              <div>
                <h3 className="text-lg font-medium mb-4">Informations des voyageurs</h3>
                <TravelerForm
                  control={control}
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                />
              </div>
            )}

            {currentStep === 'assignment' && (
              <div>
                <h3 className="text-lg font-medium mb-4">Attribution des chambres</h3>
                <RoomAssignment
                  roomPreferences={roomPreferences}
                  travelers={travelers}
                  assignments={roomAssignments}
                  onAssign={setRoomAssignments}
                />
              </div>
            )}

            {/* Demandes spéciales */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Demandes spéciales
              </label>
              <textarea
                {...register('specialRequests')}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Ex: régime alimentaire particulier, assistance spéciale..."
              />
            </div>

            {/* Navigation entre les étapes */}
            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => {
                  if (currentStep === 'travelers') setCurrentStep('rooms');
                  if (currentStep === 'assignment') setCurrentStep('travelers');
                }}
                className={`px-4 py-2 text-gray-600 hover:text-gray-800 ${
                  currentStep === 'rooms' ? 'invisible' : ''
                }`}
              >
                Retour
              </button>
              <div>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 mr-4"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Création...' : 'Créer la réservation'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}